import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '../../lib/utils';

/* ============================================================
   Shared cell renderers for the Latio DataTable.
   DESIGN_SYSTEM.md §6/§7. Tone map in §7.
   ============================================================ */

/* ---- Helpers ---- */

/** Format a COP amount as `$90.000` (dot thousands); 0/null -> "Gratis". */
export function formatCOP(value?: number | null): string {
  if (value === null || value === undefined || value === 0) return 'Gratis';
  return `$${Math.round(value).toLocaleString('de-DE')}`;
}

export function initials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}

/** Deterministic pleasant hue from a string (for avatar / sport tile fallbacks). */
export function hueFromString(str = ''): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 360;
  return h;
}

/* ---- Money ---- */
export function Money({ value, className }: { value?: number | null; className?: string }) {
  const free = value === null || value === undefined || value === 0;
  return (
    <span className={cn('tabular-nums', free ? 'text-success font-semibold' : 'text-ink', className)}>
      {formatCOP(value)}
    </span>
  );
}

/* ---- StatusBadge (tone map) ---- */
type Tone = 'indigo' | 'success' | 'warning' | 'error' | 'neutral';

const TONE_BY_STATUS: Record<string, Tone> = {
  pending: 'indigo',
  review: 'warning',
  'in review': 'warning',
  full: 'warning',
  payment_expired: 'warning',
  approved: 'success',
  active: 'success',
  published: 'success',
  confirmed: 'success',
  rejected: 'error',
  suspended: 'error',
  cancelled: 'error',
  canceled: 'error',
  flagged: 'error',
  removed: 'error', // FEAT-030: player taken off a group class
  completed: 'neutral',
  draft: 'neutral',
};

const TONE_STYLES: Record<Tone, { wrap: string; dot: string }> = {
  indigo: { wrap: 'bg-indigo-tint text-indigo', dot: 'bg-indigo' },
  success: { wrap: 'bg-success-bg text-success', dot: 'bg-success' },
  warning: { wrap: 'bg-warning-bg text-warning', dot: 'bg-warning' },
  error: { wrap: 'bg-error-bg text-error', dot: 'bg-error' },
  neutral: { wrap: 'bg-surface-2 text-ink-2', dot: 'bg-ink-3' },
};

export function statusTone(status?: string): Tone {
  if (!status) return 'neutral';
  return TONE_BY_STATUS[status.toLowerCase()] ?? 'neutral';
}

export function StatusBadge({ status, className }: { status?: string; className?: string }) {
  const tone = statusTone(status);
  const styles = TONE_STYLES[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-semibold capitalize',
        styles.wrap,
        className,
      )}
    >
      <span className={cn('size-1.5 rounded-full', styles.dot)} aria-hidden />
      {status || '—'}
    </span>
  );
}

/* ---- Avatar + Name ---- */
export function Avatar({
  src,
  name,
  size = 36,
  className,
}: {
  src?: string;
  name?: string;
  size?: number;
  className?: string;
}) {
  const hue = hueFromString(name);
  if (src) {
    return (
      <img
        src={src}
        alt={name || ''}
        width={size}
        height={size}
        className={cn('shrink-0 rounded-pill object-cover', className)}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className={cn('flex shrink-0 items-center justify-center rounded-pill font-semibold', className)}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        backgroundColor: `hsl(${hue} 60% 92%)`,
        color: `hsl(${hue} 55% 38%)`,
      }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

export function AvatarName({
  src,
  name,
  subtext,
  size = 36,
}: {
  src?: string;
  name?: string;
  subtext?: string;
  size?: number;
}) {
  return (
    <div className="flex items-center gap-3">
      <Avatar src={src} name={name} size={size} />
      <div className="min-w-0">
        <div className="truncate font-semibold text-ink">{name || '—'}</div>
        {subtext && <div className="truncate text-[13px] text-ink-2">{subtext}</div>}
      </div>
    </div>
  );
}

/* ---- SportTag (colored dot + name) ---- */
export function SportTag({ name, color }: { name?: string; color?: string }) {
  if (!name) return <span className="text-ink-3">—</span>;
  const dot = color ?? `hsl(${hueFromString(name)} 55% 50%)`;
  return (
    <span className="inline-flex items-center gap-2 text-ink">
      <span className="size-2 rounded-full" style={{ backgroundColor: dot }} aria-hidden />
      {name}
    </span>
  );
}

/* ---- RoleBadge ---- */
export function RoleBadge({ role }: { role?: string }) {
  if (!role) return <span className="text-ink-3">—</span>;
  const isCoach = /coach|trainer/i.test(role);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-pill px-2.5 py-1 text-xs font-semibold capitalize',
        isCoach ? 'bg-indigo-tint text-indigo' : 'bg-surface-2 text-ink-2',
      )}
    >
      {role}
    </span>
  );
}

/* ---- Stars ---- */
export function Stars({ value = 0, showValue = true }: { value?: number; showValue?: boolean }) {
  const rounded = Math.round(value);
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-flex">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={cn('size-3.5', i < rounded ? 'fill-orange text-orange' : 'text-hairline')}
          />
        ))}
      </span>
      {showValue && <span className="ml-1 text-[13px] font-semibold text-ink">{value.toFixed(1)}</span>}
    </span>
  );
}

/* ---- Capacity bar ---- */
export function Capacity({ filled = 0, total = 0 }: { filled?: number; total?: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((filled / total) * 100)) : 0;
  const isFull = total > 0 && filled >= total;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-pill bg-surface-2">
        <div
          className={cn('h-full rounded-pill', isFull ? 'bg-warning' : 'bg-indigo')}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[13px] tabular-nums text-ink-2">
        {filled}/{total}
      </span>
    </div>
  );
}

/* ---- RowActions — wrapper that stops row navigation (DESIGN_SYSTEM §6) ---- */
export function RowActions({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex items-center justify-end gap-1.5"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      {children}
    </div>
  );
}
